export const TAXA_STT = 16_000;

/**
 * Reamostra para 16 kHz mono por média de janela.
 *
 * É a taxa que a Groq recomenda, e mandar 48 kHz seria triplicar o tamanho do
 * envio sem melhorar a transcrição — o modelo reamostra do outro lado de
 * qualquer jeito. A média suaviza o serrilhado que o descarte puro deixaria.
 */
export function reamostrar(entrada: Float32Array, taxaOrigem: number): Float32Array {
  if (taxaOrigem === TAXA_STT || entrada.length === 0) return entrada;

  const razao = taxaOrigem / TAXA_STT;
  const saida = new Float32Array(Math.floor(entrada.length / razao));
  for (let i = 0; i < saida.length; i += 1) {
    const inicio = Math.floor(i * razao);
    const fim = Math.min(entrada.length, Math.floor((i + 1) * razao));
    let soma = 0;
    for (let j = inicio; j < fim; j += 1) soma += entrada[j] as number;
    saida[i] = fim > inicio ? soma / (fim - inicio) : 0;
  }
  return saida;
}

/** Converte para inteiro de 16 bits, que é o que o WAV carrega. */
export function paraInt16(amostras: Float32Array): Int16Array {
  const saida = new Int16Array(amostras.length);
  for (let i = 0; i < amostras.length; i += 1) {
    const valor = Math.max(-1, Math.min(1, amostras[i] as number));
    saida[i] = valor < 0 ? valor * 0x8000 : valor * 0x7fff;
  }
  return saida;
}

/**
 * Monta um WAV de 16 bits mono. Sem biblioteca: o cabeçalho tem 44 bytes e
 * uma dependência a mais no navegador custa mais do que estas vinte linhas.
 */
export function bytesDoWav(amostras: Int16Array, taxa = TAXA_STT): ArrayBuffer {
  const buffer = new ArrayBuffer(44 + amostras.length * 2);
  const visao = new DataView(buffer);

  const texto = (posicao: number, valor: string): void => {
    for (let i = 0; i < valor.length; i += 1) visao.setUint8(posicao + i, valor.charCodeAt(i));
  };

  texto(0, 'RIFF');
  visao.setUint32(4, 36 + amostras.length * 2, true);
  texto(8, 'WAVE');
  texto(12, 'fmt ');
  visao.setUint32(16, 16, true); // tamanho do bloco de formato
  visao.setUint16(20, 1, true); // PCM
  visao.setUint16(22, 1, true); // mono
  visao.setUint32(24, taxa, true);
  visao.setUint32(28, taxa * 2, true); // bytes por segundo
  visao.setUint16(32, 2, true); // alinhamento
  visao.setUint16(34, 16, true); // bits por amostra
  texto(36, 'data');
  visao.setUint32(40, amostras.length * 2, true);

  for (let i = 0; i < amostras.length; i += 1) {
    visao.setInt16(44 + i * 2, amostras[i] as number, true);
  }
  return buffer;
}

export function montarWav(amostras: Int16Array, taxa = TAXA_STT): Blob {
  return new Blob([bytesDoWav(amostras, taxa)], { type: 'audio/wav' });
}
